import { useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useForm } from 'react-hook-form';

import { TenantProvider } from '../../types';
import { tenant_provider_field_options } from './tenantSlice';

export type TenantProviderModalProps = {
  show: boolean;
  mode: 'new'|'edit'|'view';
  init?: TenantProvider;
  onClose: (data?: TenantProvider) => void;
  onRemove?: (data: TenantProvider) => void;
};

const initialState: TenantProvider = {
  name: 'google',
  can_signup: true,
  can_login: true,
  client_id: '',
  client_secret: '',
};

export function TenantProviderModal({ mode, show, init, onClose, onRemove }: TenantProviderModalProps) {
  const { register, reset, watch, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { ...initialState, ...init },
  });

  const provider_name = watch('name');

  useEffect(() => {
    if (show) {
      reset({ ...initialState, ...init });
    }
  }, [show, reset, init]);

  const onSubmit = (provider: TenantProvider) => onClose(provider);
  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        {mode === 'new' ?
          <Modal.Title>Add Provider</Modal.Title>
          :
         mode === 'edit' ?
          <Modal.Title>Update Provider</Modal.Title>
          :
          <Modal.Title>Provider Info</Modal.Title>
        }
      </Modal.Header>
      <Modal.Body>
        <form>
          <fieldset>
            <div className="mb-3 row">
              <label htmlFor="name" className="col-sm-3 col-form-label">Name</label>
              <div className="col-sm-6">
                {mode !== 'view' ?
                <select id="name" className="form-select"
                  { ...register('name', tenant_provider_field_options.name) }
                >
                  <option value="google">Google</option>
                  <option value="facebook">Facebook</option>
                  <option value="line">LINE</option>
                  <option value="email">Email</option>
                </select>
                :
                <span id="name" className="form-control-plaintext">{init?.name}</span>
                }
              </div>
            </div>
            <div className="mb-3 row">
              <label htmlFor="can_signup" className="col-sm-3 col-form-label">Can Signup</label>
              <div className="mt-2 col-sm-6">
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input id="can_signup" type="checkbox" className="form-check-input"
                    { ...register('can_signup', tenant_provider_field_options.can_signup) }
                  />
                  :
                  <input id="can_signup" readOnly type="checkbox" className="form-check-input"
                    checked={init?.can_signup ?? initialState.can_signup}/>
                  }
                </div>
              </div>
              <span className="col-sm-3 text-danger py-1">{errors.can_signup?.message}</span>
            </div>
            <div className="mb-3 row">
              <label htmlFor="can_login" className="col-sm-3 col-form-label">Can Login</label>
              <div className="mt-2 col-sm-6">
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input id="can_login" type="checkbox" className="form-check-input"
                    { ...register('can_login', tenant_provider_field_options.can_login) }
                  />
                  :
                  <input id="can_login" readOnly type="checkbox" className="form-check-input"
                    checked={init?.can_login ?? initialState.can_login}/>
                  }
                </div>
              </div>
              <span className="col-sm-3 text-danger py-1">{errors.can_login?.message}</span>
            </div>
            {provider_name !== 'email' &&
            <>
              <div className="mb-3 row">
                <label htmlFor="client_id" className="col-sm-3 col-form-label">ClientId</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="client_id" className="form-control"
                    { ...register('client_id', tenant_provider_field_options.client_id) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.client_id}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.client_id?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="client_secret" className="col-sm-3 col-form-label">ClientSecret</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="text" id="client_secret" className="form-control"
                    { ...register('client_secret', tenant_provider_field_options.client_secret) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.client_secret}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.client_secret?.message}</span>
              </div>
            </>}
            {provider_name === 'facebook' &&
            <div className="mb-3 row">
              <label htmlFor="email_is_required_for_signup" className="col-sm-3 col-form-label">Email is required for SignUp</label>
              <div className="mt-2 col-sm-6">
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input id="email_is_required_for_signup" type="checkbox" className="form-check-input"
                    { ...register('email_is_required_for_signup', tenant_provider_field_options.email_is_required_for_signup) }
                  />
                  :
                  <input id="can_login" readOnly type="checkbox" className="form-check-input"
                    checked={init?.email_is_required_for_signup ?? false}/>
                  }
                </div>
              </div>
              <span className="col-sm-3 text-danger py-1">{errors.email_is_required_for_signup?.message}</span>
            </div>
            }
            {provider_name === 'email' &&
            <>
              <div className="mb-3 row">
                <label htmlFor="allow_signup_with_password" className="col-sm-3 col-form-label">Allow signup with password</label>
                <div className="mt-2 col-sm-6">
                  <div className="col-sm-6">
                    {mode !== 'view' ?
                    <input id="allow_signup_with_password" type="checkbox" className="form-check-input"
                      { ...register('allow_signup_with_password', tenant_provider_field_options.allow_signup_with_password) }
                    />
                    :
                    <input id="can_login" readOnly type="checkbox" className="form-check-input"
                      checked={init?.allow_signup_with_password ?? false}/>
                    }
                  </div>
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.allow_signup_with_password?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="otp_digits" className="col-sm-3 col-form-label">OTP Digits</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="number" id="otp_digits" className="form-control"
                    { ...register('otp_digits', tenant_provider_field_options.otp_digits) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.otp_digits}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.otp_digits?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="otp_expiry_time" className="col-sm-3 col-form-label">OTP Expiry Time</label>
                <div className="col-sm-6">
                  {mode !== 'view' ?
                  <input type="number" id="otp_expiry_time" className="form-control"
                    { ...register('otp_expiry_time', tenant_provider_field_options.otp_expiry_time) }
                  />
                  :
                  <span className="form-control-plaintext">{init?.otp_expiry_time}</span>
                  }
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.otp_expiry_time?.message}</span>
              </div>
              <div className="mb-3 row">
                <label htmlFor="allow_passwordless" className="col-sm-3 col-form-label">Allow Passwordless</label>
                <div className="mt-2 col-sm-6">
                  <div className="col-sm-6">
                    {mode !== 'view' ?
                    <input id="allow_passwordless" type="checkbox" className="form-check-input"
                      { ...register('allow_passwordless', tenant_provider_field_options.allow_passwordless) }
                    />
                    :
                    <input id="can_login" readOnly type="checkbox" className="form-check-input"
                      checked={init?.allow_passwordless ?? false}/>
                    }
                  </div>
                </div>
                <span className="col-sm-3 text-danger py-1">{errors.allow_passwordless?.message}</span>
              </div>
            </>
            }
          </fieldset>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={() => onClose()}>
          Close
        </button>
        {mode === 'edit' ?
        <button type="submit" className="btn btn-danger" onClick={() => onRemove && onRemove(init ?? initialState)}>
          Remove
        </button>
        : null}
        {mode !== 'view' ?
        <button type="submit" className="btn btn-primary" onClick={(e) => handleSubmit(onSubmit)(e)}>
          Confirm
        </button>
        : null}
      </Modal.Footer>
    </Modal>
  );
}
